import { Link } from "react-router-dom";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { MetricCard } from "../components/dashboard";
import { Icon } from "../components/icons";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useAppContext } from "../hooks/useAppContext";

/**
 * Tickets — the connected server's support ticket queue. Layout uses the ODIN
 * design system; the stat row, toolbar and table are driven by real data with
 * honest empty states until a server is connected. No fabricated tickets.
 */

const COLUMNS = ["Ticket", "Subject", "Opened by", "Status"] as const;

export function Tickets(): JSX.Element {
  const status = usePlatformStatus();
  const { guildName } = useAppContext();
  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Tickets</h1>
            <p className="ax-head__sub">Support requests from your members, triaged and tracked to resolution.</p>
          </div>
          <Link to="/app/servers" className="ax-btn ax-btn--secondary">
            {connected ? "New ticket" : "Connect a server"}
          </Link>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {ready ? (
          <>
            <div className="ax-stats4">
              <MetricCard label="Open" icon={<Icon.tickets />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Awaiting reply" icon={<Icon.bell />} iconTone="warning" value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Resolved (7d)" icon={<Icon.check />} iconTone="success" value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Avg. first response" icon={<Icon.analytics />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
            </div>

            <div className="ax-toolbar">
              <span className="ax-input ax-input--disabled" aria-disabled="true">
                <Icon.search />
                Search tickets…
              </span>
              <span className="ax-btn ax-btn--secondary" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                All statuses
              </span>
              <span className="ax-btn ax-btn--secondary" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                Assignee
              </span>
            </div>

            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="ax-table__empty" colSpan={COLUMNS.length}>
                      {connected
                        ? "No tickets yet."
                        : "Connect a Discord server to load tickets."}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
